import { useCallback, useEffect, useMemo, useState } from 'react'
import type { JSX } from 'react'

import { formatDay } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import {
  MoneyInputError,
  formatMinorForInput,
  formatMoneyForDisplay,
  formatWireForDisplay,
  parseMajorInput,
  parseMinorUnits,
} from '../money.js'
import { DataSurface } from './DataSurface.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { sentence } from '../sentence.js'

/**
 * A supplier's price list. §6.19 `SUP-002`, §6.10 `INV-011`; `ROLE-001a`, `OFF-001a`.
 *
 * **This is `SUP-002` only.** `SUP-001` (raising an order), `SUP-004` and `SUP-005` are other
 * screens and nothing here satisfies them. What this screen adds is the way a price gets on the
 * list at all: `POST /suppliers/:id/prices` had no caller in `apps/counter`, so `cl_product`-style,
 * the table was written by an API route no shopkeeper could reach.
 *
 * **The `denied` state is real and reachable.** Appendix B has no supplier-price row, so the
 * routes borrow `cost_price_view` (read) and `purchase_order_raise` (write), and the role row is
 * necessary but not enough: `ROLE-001a` makes cost visibility a per-branch grant that defaults to
 * not granted, and the server refuses every route with 403 `COST_NOT_VISIBLE` unless the caller
 * holds it at every branch they work in. A price list is nothing but cost, so there is nothing to
 * partially show. The loader signals this by throwing {@link PriceListDenied}; any other throw is a
 * failed read. The two are different sentences, and only the first has a remedy (ask the owner).
 *
 * **Every price is checked here before it is queued.** A refused write halts the queue, including
 * taking payment, so a price the server would refuse never leaves this screen: a duplicate item
 * code on the same day (409), a cost that is not exact minor units or has more than 18 digits
 * (400), a lead time that is not a whole number of days (400), an end before the start (422), a
 * price ended that has not started (a range the server refuses). The server's limits are mirrored
 * from `supplier-prices.service.ts` and `asMinor`.
 *
 * **The currency is the supplier's and cannot be typed.** `MR-01`: the server takes it from the
 * supplier row, so the body carries no currency. It is shown, and never offered as a field: a
 * supplier's currency cannot be changed while it has prices that have not ended (`OPEN.md` §4.4),
 * so nothing here implies it can.
 *
 * **A zero cost is allowed after a confirmation.** The server accepts it and `OPEN.md` §4.4 records
 * that nobody has decided whether it is a typo or a free sample. The first press echoes the figure
 * back, the second queues it; editing the cost resets the confirmation.
 *
 * **The list is unpaginated on the wire and capped on the screen.** `GET .../prices` returns every
 * row ever recorded, ordered sku then `valid_from` descending, with no sku filter and no paging.
 * Searching and the "in force today" tick are client side over the rows already read, and at most
 * {@link ROW_CAP} matching rows are rendered at once with a sentence saying how many are not shown,
 * so a 5,000 row list (`TEST-01`) is one response but not 5,000 table rows on a cheap tablet.
 *
 * **Nothing is deleted.** A price that stops applying is ended (`validTo`) or marked not available,
 * because purchase orders were priced from it. A price that has not started cannot be ended (the
 * server refuses `validTo` before `validFrom`), so no End button is drawn for one.
 */

/** Rendered at once. A sku sits in a btree key and a list can hold thousands of rows. */
export const ROW_CAP = 100

/** The server's `MAX_LEAD_TIME_DAYS`: ten centuries, the column is int4. */
const MAX_LEAD_TIME_DAYS = 36_500

/** `asMinor`'s `^\d{1,18}$`. */
const MAX_MINOR_DIGITS = 18

/** The server's `MAX_TEXT` for an item code and a description. */
const MAX_TEXT = 200

/** Thrown by `loadPrices` when the server answers 403 `COST_NOT_VISIBLE`. */
export class PriceListDenied extends Error {}

export interface SupplierPriceData {
  readonly id: string
  readonly sku: string
  readonly description: string
  /** Minor units as a digit string: JSON has no bigint. */
  readonly unitCostMinor: string
  readonly currency: string
  readonly isAvailable: boolean
  readonly leadTimeDays: number | null
  /** `YYYY-MM-DD`. */
  readonly validFrom: string
  /** `YYYY-MM-DD`, or null for a price with no end. */
  readonly validTo: string | null
  readonly version: number
}

export interface SupplierPricesProps {
  /** The supplier row this list belongs to. Its currency is the only currency a price can have. */
  readonly supplier: {
    readonly id: string
    readonly name: string
    readonly currency: string
  }
  /**
   * Appendix B `cost_price_view`. False renders the denied wall without asking the server. True is
   * not enough, because the per-branch grant is checked server side: see {@link PriceListDenied}.
   */
  readonly canView: boolean
  /** Appendix B `purchase_order_raise`, which the create and change routes both take. */
  readonly canManage: boolean
  /** The branch's today as `YYYY-MM-DD`, for "in force", "ended" and "End this price". */
  readonly today: string
  /** The branch's timezone, for `formatDay`. */
  readonly timezone: string
  readonly loadPrices: (supplierId: string) => Promise<readonly SupplierPriceData[]>
  readonly onBack?: () => void
}

interface Draft {
  readonly sku: string
  readonly description: string
  readonly cost: string
  readonly leadTime: string
  readonly validFrom: string
  readonly validTo: string
}

const EMPTY: Draft = {
  sku: '',
  description: '',
  cost: '',
  leadTime: '',
  validFrom: '',
  validTo: '',
}

/** Keys the catalogue does not carry yet, reported in `keysMissing`. Cast once, here. */
type Say = (key: string, params?: Readonly<Record<string, string | number>>) => string

export function SupplierPrices({
  supplier,
  canView,
  canManage,
  today,
  timezone,
  loadPrices,
  onBack,
}: SupplierPricesProps): JSX.Element {
  const { t, locale } = useTranslation()
  const say = t as unknown as Say
  const { enqueue, snapshot } = useWriteQueue()
  const [rows, setRows] = useState<readonly SupplierPriceData[] | null>(null)
  // Apart from `error`, because a bad cost in a form must not turn the list into a failed read.
  const [loadError, setLoadError] = useState<string | null>(null)
  const [denied, setDenied] = useState(false)
  const [search, setSearch] = useState('')
  const [onlyCurrent, setOnlyCurrent] = useState(false)
  const [mode, setMode] = useState<'closed' | 'add' | string>('closed')
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [error, setError] = useState<string | null>(null)
  const [confirmZero, setConfirmZero] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  const currency = supplier.currency

  const refresh = useCallback(async () => {
    if (!canView) return
    try {
      const loaded = await loadPrices(supplier.id)
      setRows(loaded)
      setLoadError(null)
      setDenied(false)
    } catch (failure) {
      setRows(null)
      if (failure instanceof PriceListDenied) {
        setDenied(true)
        setLoadError(null)
      } else {
        setDenied(false)
        setLoadError(t('supplierPrices.loadFailed'))
      }
    }
  }, [canView, loadPrices, supplier.id, t])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setMode('closed')
    setDraft(EMPTY)
    setError(null)
    setConfirmZero(null)
    void refresh()
  })

  // A write somebody discarded in `BlockedWrites` never reaches `sent`, so without this the
  // buttons stay disabled for good.
  useEffect(() => {
    if (writeId === null) return
    if (!snapshot.writes.some((candidate) => candidate.id === writeId)) setWriteId(null)
  }, [snapshot, writeId])

  const inForce = useCallback(
    (row: SupplierPriceData): boolean =>
      row.validFrom <= today && (row.validTo === null || row.validTo >= today),
    [today],
  )

  const matching = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return (rows ?? []).filter(
      (row) =>
        (!onlyCurrent || inForce(row)) &&
        (needle === '' ||
          row.sku.toLowerCase().includes(needle) ||
          row.description.toLowerCase().includes(needle)),
    )
  }, [rows, search, onlyCurrent, inForce])

  const shown = matching.slice(0, ROW_CAP)

  /** Cost text to exact minor-unit digits, or a sentence. Zero is allowed here. */
  const costOf = (text: string): { readonly minor: string } | { readonly refusal: string } => {
    if (text.trim() === '') return { refusal: t('supplierPrices.costRequired') }
    let minor: bigint
    try {
      minor = parseMajorInput(text, currency)
    } catch (failure) {
      if (failure instanceof MoneyInputError) {
        return { refusal: t('supplierPrices.costShape', { currency }) }
      }
      throw failure
    }
    if (minor < 0n) return { refusal: t('supplierPrices.costShape', { currency }) }
    if (minor.toString().length > MAX_MINOR_DIGITS) {
      return { refusal: t('supplierPrices.costTooLarge') }
    }
    return { minor: minor.toString() }
  }

  /** Lead time text to a number or null, or a sentence. */
  const leadOf = (
    text: string,
  ): { readonly days: number | null } | { readonly refusal: string } => {
    const trimmed = text.trim()
    if (trimmed === '') return { days: null }
    const days = /^\d+$/.test(trimmed) ? Number(trimmed) : Number.NaN
    if (!Number.isSafeInteger(days) || days > MAX_LEAD_TIME_DAYS) {
      return { refusal: say('supplierPrices.leadTimeShape', { max: MAX_LEAD_TIME_DAYS }) }
    }
    return { days }
  }

  /** Echo a zero back once. Returns true when the write may go ahead. */
  const confirmedZero = (minor: string): boolean => {
    if (minor !== '0') return true
    if (confirmZero === minor) return true
    setConfirmZero(minor)
    setError(null)
    return false
  }

  const queue = async (request: Parameters<typeof enqueue>[0]): Promise<void> => {
    const write = (await enqueue(request)) as { id: string }
    setWriteId(write.id)
  }

  const pricesPath = `/suppliers/${encodeURIComponent(supplier.id)}/prices`

  const add = async (): Promise<void> => {
    const sku = draft.sku.trim()
    const description = draft.description.trim()
    if (sku === '') return setError(t('supplierPrices.skuRequired'))
    if (description === '') return setError(t('supplierPrices.descriptionRequired'))
    const cost = costOf(draft.cost)
    if ('refusal' in cost) return setError(cost.refusal)
    const lead = leadOf(draft.leadTime)
    if ('refusal' in lead) return setError(lead.refusal)
    if (draft.validFrom === '') return setError(t('supplierPrices.validFromRequired'))
    if (draft.validTo !== '' && draft.validTo < draft.validFrom) {
      return setError(t('supplierPrices.rangeInvalid'))
    }
    if ((rows ?? []).some((row) => row.sku === sku && row.validFrom === draft.validFrom)) {
      // `SUPPLIER_PRICE_EXISTS`, a 409 that would hold the queue.
      return setError(
        t('supplierPrices.alreadyListed', {
          sku,
          date: formatDay(draft.validFrom, timezone, locale),
        }),
      )
    }
    if (!confirmedZero(cost.minor)) return
    setError(null)
    await queue({
      method: 'POST',
      path: pricesPath,
      label: t('supplierPrices.queueAddLabel', { sku }),
      body: {
        sku,
        description,
        unitCostMinor: cost.minor,
        leadTimeDays: lead.days,
        validFrom: draft.validFrom,
        validTo: draft.validTo === '' ? null : draft.validTo,
      },
    })
  }

  const change = async (row: SupplierPriceData): Promise<void> => {
    const description = draft.description.trim()
    if (description === '') return setError(t('supplierPrices.descriptionRequired'))
    const cost = costOf(draft.cost)
    if ('refusal' in cost) return setError(cost.refusal)
    const lead = leadOf(draft.leadTime)
    if ('refusal' in lead) return setError(lead.refusal)
    const validTo = draft.validTo === '' ? null : draft.validTo
    if (validTo !== null && validTo < row.validFrom) {
      return setError(t('supplierPrices.rangeInvalid'))
    }
    if (
      description === row.description &&
      cost.minor === row.unitCostMinor &&
      lead.days === row.leadTimeDays &&
      validTo === row.validTo
    ) {
      return setError(t('supplierPrices.nothingChanged'))
    }
    if (!confirmedZero(cost.minor)) return
    setError(null)
    await queue({
      method: 'PATCH',
      path: `${pricesPath}/${encodeURIComponent(row.id)}`,
      label: t('supplierPrices.queueChangeLabel', { sku: row.sku }),
      body: {
        description,
        unitCostMinor: cost.minor,
        leadTimeDays: lead.days,
        validTo,
        expectedVersion: row.version,
      },
    })
  }

  const endNow = async (row: SupplierPriceData): Promise<void> => {
    setError(null)
    await queue({
      method: 'PATCH',
      path: `${pricesPath}/${encodeURIComponent(row.id)}`,
      label: t('supplierPrices.queueEndLabel', { sku: row.sku }),
      body: { validTo: today, expectedVersion: row.version },
    })
  }

  const setAvailable = async (row: SupplierPriceData, isAvailable: boolean): Promise<void> => {
    setError(null)
    await queue({
      method: 'PATCH',
      path: `${pricesPath}/${encodeURIComponent(row.id)}`,
      label: t(
        isAvailable ? 'supplierPrices.queueAvailableLabel' : 'supplierPrices.queueUnavailableLabel',
        {
          sku: row.sku,
        },
      ),
      body: { isAvailable, expectedVersion: row.version },
    })
  }

  const open = (next: 'add' | SupplierPriceData): void => {
    setError(null)
    setConfirmZero(null)
    if (next === 'add') {
      setMode('add')
      setDraft(EMPTY)
      return
    }
    setMode(next.id)
    setDraft({
      sku: next.sku,
      description: next.description,
      cost: formatMinorForInput(parseMinorUnits(next.unitCostMinor), next.currency),
      leadTime: next.leadTimeDays === null ? '' : String(next.leadTimeDays),
      validFrom: next.validFrom,
      validTo: next.validTo ?? '',
    })
  }

  const close = (): void => {
    setMode('closed')
    setDraft(EMPTY)
    setError(null)
    setConfirmZero(null)
  }

  const set = (key: keyof Draft, value: string): void => {
    setDraft((was) => ({ ...was, [key]: value }))
    // A new figure is a new question: the echo was for the old one.
    if (key === 'cost' && confirmZero !== null) {
      setConfirmZero(null)
      setError(null)
    }
  }

  const field = (
    key: keyof Draft,
    label: string,
    options: { readonly type?: string; readonly ltr?: boolean; readonly max?: number } = {},
  ): JSX.Element => (
    <div className="lm-field">
      <label htmlFor={`sp-${key}`} className="lm-field__label">
        {label}
      </label>
      <input
        id={`sp-${key}`}
        className="lm-input"
        type={options.type ?? 'text'}
        {...(options.ltr === true ? { dir: 'ltr' } : { dir: 'auto' })}
        {...(options.max === undefined ? {} : { maxLength: options.max })}
        autoComplete="off"
        value={draft[key]}
        onChange={(e) => {
          set(key, e.currentTarget.value)
        }}
      />
    </div>
  )

  const form = (editing: SupplierPriceData | null): JSX.Element => (
    <div className="lm-supplier__form">
      {editing === null ? (
        <>
          {field('sku', t('supplierPrices.sku'), { ltr: true, max: MAX_TEXT })}
          {field('description', t('supplierPrices.description'), { max: MAX_TEXT })}
        </>
      ) : (
        <>
          <p className="lm-supplier__meta">{t('supplierPrices.change', { sku: editing.sku })}</p>
          {field('description', t('supplierPrices.description'), { max: MAX_TEXT })}
        </>
      )}
      {field('cost', t('supplierPrices.cost', { currency }), { ltr: true })}
      {field('leadTime', t('supplierPrices.leadTime'), { ltr: true })}
      {editing === null
        ? field('validFrom', t('supplierPrices.validFrom'), { type: 'date', ltr: true })
        : null}
      {field('validTo', t('supplierPrices.validTo'), { type: 'date', ltr: true })}
      {editing === null ? <p className="lm-hint">{t('supplierPrices.newDayHint')}</p> : null}
      {confirmZero === null ? null : (
        // The figure as a real element so it is read back exactly, then the sentence asking.
        <p className="lm-field__error" role="alert">
          <Ltr>{formatMoneyForDisplay(parseMinorUnits(confirmZero), currency)}</Ltr>{' '}
          {say('supplierPrices.zeroConfirm')}
        </p>
      )}
      {error === null ? null : (
        <p className="lm-field__error" role="alert">
          {error}
        </p>
      )}
      <button
        type="button"
        className="lm-button lm-button--primary"
        disabled={writeId !== null}
        onClick={() => {
          void (editing === null ? add() : change(editing))
        }}
      >
        {t(editing === null ? 'supplierPrices.add' : 'supplierPrices.save')}
      </button>
      <button type="button" className="lm-button lm-button--secondary" onClick={close}>
        {t('supplierPrices.cancel')}
      </button>
    </div>
  )

  const line = statusLine(t, snapshot, writeId, t('queue.subject.supplierPrice'))

  const deniedNow = !canView || denied

  const surfaceState = deniedNow
    ? ({ kind: 'denied' } as const)
    : loadError !== null
      ? ({ kind: 'error', message: loadError, onRetry: () => void refresh() } as const)
      : rows === null
        ? ({ kind: 'loading' } as const)
        : rows.length === 0
          ? ({ kind: 'empty' } as const)
          : matching.length === 0
            ? ({ kind: 'filtered' } as const)
            : ({ kind: 'ready' } as const)

  const dayRange = (row: SupplierPriceData): JSX.Element =>
    row.validTo === null
      ? sentence(t, 'supplierPrices.dates', {
          from: <Ltr>{formatDay(row.validFrom, timezone, locale)}</Ltr>,
        })
      : sentence(t, 'supplierPrices.datesTo', {
          from: <Ltr>{formatDay(row.validFrom, timezone, locale)}</Ltr>,
          to: <Ltr>{formatDay(row.validTo, timezone, locale)}</Ltr>,
        })

  const stateOf = (row: SupplierPriceData): string => {
    if (!row.isAvailable) return t('supplierPrices.notAvailable')
    if (row.validFrom > today) {
      return t('supplierPrices.notStarted', { date: formatDay(row.validFrom, timezone, locale) })
    }
    if (row.validTo !== null && row.validTo < today) return t('supplierPrices.ended')
    return t('supplierPrices.inForce')
  }

  return (
    <section className="lm-screen" aria-labelledby="supplier-prices-heading">
      <h1 id="supplier-prices-heading" className="lm-screen__heading">
        {t('supplierPrices.heading', { name: supplier.name })}
      </h1>

      {onBack === undefined ? null : (
        <button type="button" className="lm-button lm-button--secondary" onClick={onBack}>
          {t('supplierPrices.back')}
        </button>
      )}

      {/* The currency is stated and never offered as a field (`OPEN.md` §4.4). */}
      <p className="lm-hint">
        {t('supplierPrices.currencyHint', { currency, name: supplier.name })}
      </p>

      {deniedNow ? null : (
        <div className="lm-supplier__form">
          <div className="lm-field">
            <label htmlFor="sp-search" className="lm-field__label">
              {t('supplierPrices.search')}
            </label>
            <input
              id="sp-search"
              className="lm-input"
              type="search"
              dir="auto"
              autoComplete="off"
              value={search}
              onChange={(e) => {
                setSearch(e.currentTarget.value)
              }}
            />
          </div>
          <div className="lm-field">
            <label htmlFor="sp-current" className="lm-field__label">
              <input
                id="sp-current"
                type="checkbox"
                checked={onlyCurrent}
                onChange={(e) => {
                  setOnlyCurrent(e.currentTarget.checked)
                }}
              />
              {t('supplierPrices.onlyCurrent')}
            </label>
          </div>
        </div>
      )}

      {/*
        §8 `ST-02`. Search and the add form stay outside the surface so a shop with no prices still
        has the way to add one. Failed and empty are different states: only one is ever true.
      */}
      <DataSurface
        state={surfaceState}
        shape="rows"
        rows={4}
        what={t('supplierPrices.what')}
        emptyMessage={canManage ? t('supplierPrices.none') : t('supplierPrices.noneReadOnly')}
        filteredMessage={t('supplierPrices.filtered')}
        deniedMessage={t('supplierPrices.denied')}
      >
        <table className="lm-line-table">
          <thead>
            <tr>
              <th scope="col">{t('supplierPrices.colCode')}</th>
              <th scope="col">{t('supplierPrices.colWhat')}</th>
              <th scope="col">{t('supplierPrices.colCost')}</th>
              <th scope="col">{t('supplierPrices.colLead')}</th>
              <th scope="col">{t('supplierPrices.colDates')}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => {
              const startsLater = row.validFrom > today
              const stillRunning = row.validTo === null || row.validTo > today
              return (
                <tr key={row.id} className="lm-supplier-price">
                  <th scope="row" className="lm-line-table__description" dir="ltr">
                    <Ltr>{row.sku}</Ltr>
                  </th>
                  <td dir="auto">
                    {row.description}
                    <span className="lm-line-table__meta">{stateOf(row)}</span>
                  </td>
                  <td className="lm-line-table__num" dir="ltr">
                    {formatWireForDisplay(row.unitCostMinor, row.currency)}
                  </td>
                  <td className="lm-line-table__num" dir="ltr">
                    {row.leadTimeDays === null ? '' : row.leadTimeDays}
                  </td>
                  <td>
                    {dayRange(row)}
                    {canManage ? (
                      <>
                        <button
                          type="button"
                          className="lm-button lm-button--secondary"
                          disabled={writeId !== null}
                          onClick={() => {
                            open(row)
                          }}
                        >
                          {t('supplierPrices.change', { sku: row.sku })}
                        </button>
                        <button
                          type="button"
                          className="lm-button lm-button--secondary"
                          disabled={writeId !== null}
                          onClick={() => {
                            void setAvailable(row, !row.isAvailable)
                          }}
                        >
                          {t(
                            row.isAvailable
                              ? 'supplierPrices.markUnavailable'
                              : 'supplierPrices.markAvailable',
                            { sku: row.sku },
                          )}
                        </button>
                        {/* A price that has not started cannot be ended: the server refuses a
                            `validTo` before `validFrom`. One already ended has nothing to end. */}
                        {!startsLater && stillRunning ? (
                          <button
                            type="button"
                            className="lm-button lm-button--secondary"
                            disabled={writeId !== null}
                            onClick={() => {
                              void endNow(row)
                            }}
                          >
                            {t('supplierPrices.endIt')}
                            <span className="lm-visually-hidden"> {row.sku}</span>
                          </button>
                        ) : null}
                        {mode === row.id ? form(row) : null}
                      </>
                    ) : null}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {matching.length > shown.length ? (
          <p className="lm-hint" role="status">
            {say('supplierPrices.showingFirst', { shown: shown.length, total: matching.length })}
          </p>
        ) : null}
      </DataSurface>

      {canManage && !deniedNow && rows !== null ? (
        mode === 'add' ? (
          form(null)
        ) : (
          <button
            type="button"
            className="lm-button lm-button--primary"
            onClick={() => {
              open('add')
            }}
          >
            {t('supplierPrices.addOne')}
          </button>
        )
      ) : null}

      {line === null ? null : <p className="lm-hint">{line}</p>}
    </section>
  )
}
